import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Loader2,
  PackageCheck,
  RotateCcw,
  Search,
  Send,
  Truck,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import {
  Field,
  FieldError,
  FieldLabel,
} from '@quebon/ui/components/field'
import { Spinner } from '@quebon/ui/components/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'
import { Textarea } from '@quebon/ui/components/textarea'
import {
  textbookRequestsApi,
  type AdminTextbookRequestStatusUpdateBody,
  type AdminTextbookRequestView,
  type TextbookRequestStatus,
} from '@/api/textbookRequests'
import { handleApiError } from '@/api/client'
import { HappydayDispatchDialog } from './HappydayDispatchDialog'
import {
  TextbookStatusActionDialog,
  type TextbookStatusActionDetails,
} from './TextbookStatusActionDialog'
import { TextbookStatusHistoryDialog } from './TextbookStatusHistoryDialog'
import { formatHappydayResponseMessage } from './happydayResponseMessage'
import { formatTextbookRequestDate } from './textbookRequestDate'
import {
  filterTextbookRequestGroups,
  groupTextbookRequestsByCenter,
  summarizeHappydayItems,
  type TextbookRequestCenterGroup,
} from './textbookRequestGroups'
import { TEXTBOOK_STATUS_LABELS, textbookRequestBookLabel } from './textbookRequestStatus'

const STATUS_STYLES: Record<TextbookRequestStatus, string> = {
  REQUESTED_TO_HQ: 'bg-amber-100 text-amber-700',
  OUTBOUND_REQUESTING: 'bg-indigo-100 text-indigo-700',
  OUTBOUND_REQUESTED: 'bg-cyan-100 text-cyan-700',
  OUTBOUND_UNCERTAIN: 'bg-orange-100 text-orange-700',
  SHIPPED: 'bg-blue-100 text-blue-700',
  RECEIVED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-slate-100 text-slate-500',
  REJECTED: 'bg-red-100 text-red-700',
}

const STATUS_OPTIONS: Array<{ value: '' | TextbookRequestStatus; label: string }> = [
  { value: '', label: '전체 상태' },
  { value: 'REQUESTED_TO_HQ', label: '요청됨' },
  { value: 'OUTBOUND_REQUESTING', label: '출고 요청 중' },
  { value: 'OUTBOUND_REQUESTED', label: '출고 요청 완료' },
  { value: 'OUTBOUND_UNCERTAIN', label: '접수 확인 필요' },
  { value: 'SHIPPED', label: '발송됨' },
  { value: 'RECEIVED', label: '수령 완료' },
  { value: 'REJECTED', label: '거절됨' },
  { value: 'CANCELLED', label: '요청 취소' },
]

function HappydayResponseSummary({ message }: { message: string }) {
  const displayMessage = formatHappydayResponseMessage(message)

  return (
    <p className="mt-1 line-clamp-2 text-[11px] text-slate-500" title={displayMessage}>
      {displayMessage}
    </p>
  )
}

interface OutboundRequestedActionsProps {
  updatePending: boolean
  onConfirmShipped: () => void
  onCancel: () => void
}

export function OutboundRequestedActions({
  updatePending,
  onConfirmShipped,
  onCancel,
}: OutboundRequestedActionsProps) {
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={updatePending}
        onClick={onConfirmShipped}
      >
        <Truck data-icon="inline-start" />
        발송 확인
      </Button>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        disabled={updatePending}
        onClick={onCancel}
      >
        <XCircle data-icon="inline-start" />
        취소 처리
      </Button>
    </>
  )
}

interface RequestedCenterCardProps {
  group: TextbookRequestCenterGroup
  updatePending: boolean
  onDispatch: () => void
  onRejectAll: () => void
  onUpdate: (request: AdminTextbookRequestView, nextStatus: 'SHIPPED' | 'REJECTED') => void
  onHistory: (request: AdminTextbookRequestView) => void
}

export function RequestedCenterCard({
  group,
  updatePending,
  onDispatch,
  onRejectAll,
  onUpdate,
  onHistory,
}: RequestedCenterCardProps) {
  const happydayEnabled = group.requests.every((request) => request.happydayEnabled)
  const isTestCenter = group.requests.some((request) => request.organizationOperationMode === 'TEST')
  const learnerCount = new Set(group.requests.map((request) => request.learnerUserId)).size
  const itemCount = summarizeHappydayItems(group.requests).length

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{group.organizationName ?? '-'}</span>
          <Badge variant="secondary">요청 {group.requests.length}건</Badge>
          {isTestCenter ? <Badge variant="destructive">TEST 센터</Badge> : null}
        </CardTitle>
        <CardDescription>
          {group.organizationSubdomain ?? `센터 #${group.organizationId}`}
          {' · '}학습자 {learnerCount}명 · 출고 상품 {itemCount}종
        </CardDescription>
        {isTestCenter || happydayEnabled ? (
          <CardAction>
            <div className="flex gap-2">
              {isTestCenter ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={updatePending}
                  onClick={onRejectAll}
                >
                  <XCircle data-icon="inline-start" />
                  센터 요청 일괄 거절
                </Button>
              ) : null}
              {happydayEnabled ? (
                <Button type="button" disabled={updatePending} onClick={onDispatch}>
                  <Send data-icon="inline-start" />
                  센터 출고 요청
                </Button>
              ) : null}
            </div>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>학생</TableHead>
              <TableHead>교재</TableHead>
              <TableHead>요청일</TableHead>
              <TableHead>최근 응답</TableHead>
              <TableHead className="text-right">개별 처리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <div className="font-medium">{request.learnerName ?? `#${request.learnerUserId}`}</div>
                  <div className="text-xs text-muted-foreground">{request.learnerEmail ?? '-'}</div>
                </TableCell>
                <TableCell className="max-w-80 whitespace-normal">
                  <div className="font-medium">{textbookRequestBookLabel(request)}</div>
                  {request.happydayItemCode != null || request.happydayIsbnCode ? (
                    <div className="text-xs text-muted-foreground">
                      {request.happydayItemCode != null
                        ? `상품코드 ${request.happydayItemCode}`
                        : `ISBN ${request.happydayIsbnCode}`}
                    </div>
                  ) : (
                    <Badge variant="destructive">상품 매핑 없음</Badge>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">{formatTextbookRequestDate(request.requestedAt)}</TableCell>
                <TableCell className="max-w-72 whitespace-normal">
                  {request.happydayResponseMessage ? (
                    <HappydayResponseSummary message={request.happydayResponseMessage} />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    {!happydayEnabled ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={updatePending}
                        onClick={() => onUpdate(request, 'SHIPPED')}
                      >
                        <Truck data-icon="inline-start" />
                        수동 발송
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={updatePending}
                      onClick={() => onUpdate(request, 'REJECTED')}
                    >
                      <XCircle data-icon="inline-start" />
                      거절
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={updatePending}
                      onClick={() => onHistory(request)}
                    >
                      <History data-icon="inline-start" />
                      이력
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export function TextbookRequestsPage() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'' | TextbookRequestStatus>('REQUESTED_TO_HQ')
  const [search, setSearch] = useState('')
  const [dispatchGroup, setDispatchGroup] = useState<TextbookRequestCenterGroup | null>(null)
  const [rejectGroup, setRejectGroup] = useState<TextbookRequestCenterGroup | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectReasonInvalid, setRejectReasonInvalid] = useState(false)
  const [statusAction, setStatusAction] = useState<{
    request: AdminTextbookRequestView
    nextStatus: AdminTextbookRequestStatusUpdateBody['status']
  } | null>(null)
  const [historyTarget, setHistoryTarget] = useState<AdminTextbookRequestView | null>(null)

  const { data: requests = [], isLoading, isError } = useQuery({
    queryKey: ['admin', 'textbook-requests', status],
    queryFn: () => textbookRequestsApi.list(status || undefined),
  })

  const invalidateRequests = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'textbook-requests'] })

  const updateMutation = useMutation({
    mutationFn: ({
      request,
      nextStatus,
      details,
    }: {
      request: AdminTextbookRequestView
      nextStatus: AdminTextbookRequestStatusUpdateBody['status']
      details: TextbookStatusActionDetails
    }) => textbookRequestsApi.updateStatus(request.id, {
      status: nextStatus,
      expectedStatus: request.status,
      attemptId: crypto.randomUUID(),
      reason: details.reason,
      memo: null,
      statusMessage: details.statusMessage,
      externalConfirmed: details.externalConfirmed,
      externalReference: details.externalReference,
    }),
    onSuccess: () => {
      setStatusAction(null)
      void invalidateRequests()
      toast.success('교재 요청 상태를 변경했습니다.')
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return requests
    return requests.filter((request) => {
      const haystack = [
        request.organizationName,
        request.organizationSubdomain,
        request.learnerName,
        request.learnerEmail,
        request.bookTitle,
        request.volumeLabel,
        request.triggerLessonTitle,
      ].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(keyword)
    })
  }, [requests, search])
  const requestedCenterGroups = useMemo(
    () => filterTextbookRequestGroups(groupTextbookRequestsByCenter(requests), search),
    [requests, search],
  )
  const isRequestedStatus = status === 'REQUESTED_TO_HQ'

  const batchRejectMutation = useMutation({
    mutationFn: (group: TextbookRequestCenterGroup) => {
      const reason = rejectReason.trim()
      if (!reason) throw new Error('Bulk rejection reason is required')
      return textbookRequestsApi.rejectTestCenterRequests({
        requestIds: group.requests.map((request) => request.id),
        attemptId: crypto.randomUUID(),
        reason,
      })
    },
    onSuccess: (rejected, group) => {
      setRejectGroup(null)
      setRejectReason('')
      setRejectReasonInvalid(false)
      void invalidateRequests()
      toast.success(`${group.organizationName ?? 'TEST 센터'} 교재 요청 ${rejected.length}건을 거절했습니다.`)
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const resultCount = isRequestedStatus ? requestedCenterGroups.length : filtered.length
  const primaryDispatchRequest = dispatchGroup?.requests[0] ?? null

  const openStatusAction = (
    request: AdminTextbookRequestView,
    nextStatus: AdminTextbookRequestStatusUpdateBody['status'],
  ) => setStatusAction({ request, nextStatus })

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PackageCheck className="h-7 w-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">교재 요청 관리</h1>
            <p className="text-sm text-slate-500">러닝센터 요청을 해피데이에 전달하고 실제 발송·수령을 추적합니다.</p>
          </div>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-[220px_1fr]">
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as '' | TextbookRequestStatus)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <label className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="센터, 학생, 교재, 레슨 검색"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm"
          />
        </label>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : isError ? (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
          <AlertTriangle className="h-5 w-5" />
          <span>교재 요청을 불러오지 못했습니다.</span>
        </div>
      ) : resultCount === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-16 text-center text-sm text-slate-500">
          조건에 맞는 교재 요청이 없습니다.
        </div>
      ) : isRequestedStatus ? (
        <div className="flex flex-col gap-4">
          {requestedCenterGroups.map((group) => (
            <RequestedCenterCard
              key={group.organizationId}
              group={group}
              updatePending={updateMutation.isPending || batchRejectMutation.isPending}
              onDispatch={() => setDispatchGroup(group)}
              onRejectAll={() => {
                setRejectReason('')
                setRejectReasonInvalid(false)
                setRejectGroup(group)
              }}
              onUpdate={openStatusAction}
              onHistory={setHistoryTarget}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="min-w-[1100px]">
            <div className="grid grid-cols-[150px_minmax(180px,1fr)_minmax(180px,1.2fr)_150px_140px_220px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase text-slate-400">
              <div>센터</div>
              <div>학생</div>
              <div>교재</div>
              <div>상태</div>
              <div>요청일</div>
              <div className="text-right">처리</div>
            </div>
            <div className="divide-y divide-slate-100">
              {filtered.map((request) => (
                <div key={request.id} className="grid grid-cols-[150px_minmax(180px,1fr)_minmax(180px,1.2fr)_150px_140px_220px] items-center gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{request.organizationName ?? '-'}</div>
                    <div className="truncate text-xs text-slate-400">{request.organizationSubdomain ?? `#${request.organizationId}`}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{request.learnerName ?? `#${request.learnerUserId}`}</div>
                    <div className="truncate text-xs text-slate-400">{request.learnerEmail ?? '-'}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{textbookRequestBookLabel(request)}</div>
                    <div className="truncate text-xs text-slate-400">
                      {request.triggerLessonTitle ? `기준 레슨: ${request.triggerLessonTitle}` : '수동 요청'}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[request.status]}`}>
                      {TEXTBOOK_STATUS_LABELS[request.status]}
                    </span>
                    {request.happydayResponseMessage ? (
                      <HappydayResponseSummary message={request.happydayResponseMessage} />
                    ) : null}
                  </div>
                  <div className="text-xs text-slate-500">{formatTextbookRequestDate(request.requestedAt)}</div>
                  <div className="flex flex-wrap justify-end gap-1">
                    {request.status === 'REQUESTED_TO_HQ' ? (
                      <>
                        {request.happydayEnabled ? (
                          <Button
                            type="button"
                            onClick={() => setStatus('REQUESTED_TO_HQ')}
                            variant="outline"
                            size="sm"
                          >
                            <Send data-icon="inline-start" />
                            센터별 처리
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            disabled={updateMutation.isPending}
                            onClick={() => openStatusAction(request, 'SHIPPED')}
                            variant="outline"
                            size="sm"
                            title="해피데이 연동이 비활성화되어 기존 수동 처리를 사용합니다."
                          >
                            <Truck data-icon="inline-start" />
                            수동 발송
                          </Button>
                        )}
                        <Button
                          type="button"
                          disabled={updateMutation.isPending}
                          onClick={() => openStatusAction(request, 'REJECTED')}
                          variant="destructive"
                          size="sm"
                        >
                          <XCircle data-icon="inline-start" />
                          거절
                        </Button>
                      </>
                    ) : request.status === 'OUTBOUND_REQUESTED' ? (
                      <OutboundRequestedActions
                        updatePending={updateMutation.isPending}
                        onConfirmShipped={() => openStatusAction(request, 'SHIPPED')}
                        onCancel={() => openStatusAction(request, 'CANCELLED')}
                      />
                    ) : request.status === 'OUTBOUND_UNCERTAIN' || request.status === 'OUTBOUND_REQUESTING' ? (
                      <>
                        <Button
                          type="button"
                          disabled={updateMutation.isPending}
                          onClick={() => openStatusAction(request, 'OUTBOUND_REQUESTED')}
                          variant="outline"
                          size="sm"
                        >
                          <CheckCircle2 data-icon="inline-start" />
                          접수 확인
                        </Button>
                        <Button
                          type="button"
                          disabled={updateMutation.isPending}
                          onClick={() => openStatusAction(request, 'REQUESTED_TO_HQ')}
                          variant="outline"
                          size="sm"
                        >
                          <RotateCcw data-icon="inline-start" />
                          미접수 확인
                        </Button>
                      </>
                    ) : request.status === 'RECEIVED' ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        완료
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">처리 불가</span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={updateMutation.isPending}
                      onClick={() => setHistoryTarget(request)}
                    >
                      <History data-icon="inline-start" />
                      이력
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {statusAction ? (
        <TextbookStatusActionDialog
          key={`${statusAction.request.id}:${statusAction.nextStatus}`}
          request={statusAction.request}
          nextStatus={statusAction.nextStatus}
          pending={updateMutation.isPending}
          onClose={() => setStatusAction(null)}
          onConfirm={(details) => updateMutation.mutate({
            request: statusAction.request,
            nextStatus: statusAction.nextStatus,
            details,
          })}
        />
      ) : null}

      {historyTarget ? (
        <TextbookStatusHistoryDialog
          key={historyTarget.id}
          request={historyTarget}
          onClose={() => setHistoryTarget(null)}
          onCorrected={() => { void invalidateRequests() }}
        />
      ) : null}

      <Dialog
        open={rejectGroup !== null}
        onOpenChange={(open) => {
          if (!open && !batchRejectMutation.isPending) setRejectGroup(null)
        }}
      >
        <DialogContent showCloseButton={!batchRejectMutation.isPending}>
          <DialogHeader>
            <DialogTitle>{rejectGroup?.organizationName ?? 'TEST 센터'} 요청 일괄 거절</DialogTitle>
            <DialogDescription>
              이 센터에서 현재 요청됨 상태인 교재 요청만 처리합니다.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>교재 요청 {rejectGroup?.requests.length ?? 0}건</AlertTitle>
            <AlertDescription>
              이 센터의 요청은 모두 거절됨으로 변경되며 출고 대상에서 제외됩니다.
            </AlertDescription>
          </Alert>
          <Field data-invalid={rejectReasonInvalid || undefined}>
            <FieldLabel htmlFor="batch-reject-reason">거절 사유 *</FieldLabel>
            <Textarea
              id="batch-reject-reason"
              value={rejectReason}
              maxLength={1000}
              disabled={batchRejectMutation.isPending}
              aria-invalid={rejectReasonInvalid || undefined}
              onChange={(event) => {
                setRejectReason(event.target.value)
                if (event.target.value.trim()) setRejectReasonInvalid(false)
              }}
            />
            {rejectReasonInvalid ? <FieldError>거절 사유를 입력해 주세요.</FieldError> : null}
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={batchRejectMutation.isPending}
              onClick={() => setRejectGroup(null)}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={batchRejectMutation.isPending || rejectGroup === null}
              onClick={() => {
                if (!rejectReason.trim()) {
                  setRejectReasonInvalid(true)
                  return
                }
                if (rejectGroup) batchRejectMutation.mutate(rejectGroup)
              }}
            >
              {batchRejectMutation.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <XCircle data-icon="inline-start" />
              )}
              {batchRejectMutation.isPending ? '거절 처리 중' : '센터 요청 거절'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {dispatchGroup && primaryDispatchRequest ? (
        <HappydayDispatchDialog
          key={`${dispatchGroup.organizationId}:${dispatchGroup.requests.map((request) => request.id).join(',')}`}
          request={primaryDispatchRequest}
          requests={dispatchGroup.requests}
          onClose={() => setDispatchGroup(null)}
          onDispatched={() => { void invalidateRequests() }}
        />
      ) : null}
    </div>
  )
}
